import React, { useState } from 'react';
import { Copy, FileText, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { WarningBanner } from '../components/WarningBanner';
import { SEO } from '../components/SEO';
import { PageLayout } from '../components/PageLayout';
import { useLanguage } from '../hooks/useLanguage';
export function ReportTemplateGeneratorPage() {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    scamType: '',
    date: '',
    platform: '',
    amount: '',
    description: ''
  });
  const [generatedTemplate, setGeneratedTemplate] = useState('');
  const [copied, setCopied] = useState(false);
  const handleGenerate = () => {
    const template = `
SUBJECT: URGENT: Report of ${formData.scamType || 'Scam Incident'}

To Whom It May Concern,

I am writing to report a scam incident that occurred on ${formData.date || '[Date]'}.

Incident Details:
- Type of Scam: ${formData.scamType || '[Type]'}
- Platform/Method: ${formData.platform || '[Platform]'}
- Amount Lost: ${formData.amount || '[Amount]'}

Description:
${formData.description || '[Description of what happened]'}

I have preserved evidence including screenshots and transaction records. Please advise on the next steps to secure my account and investigate this matter.

Sincerely,
[Your Name]
[Your Contact Number]
    `.trim();
    setGeneratedTemplate(template);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(generatedTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const inputClasses = 'w-full rounded-lg border-slate-300 p-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 shadow-sm';
  const labelClasses = 'block text-sm font-medium text-slate-700 mb-1.5';
  return <PageLayout title={t('generator.title')} subtitle={t('generator.subtitle')} icon={<FileText className="w-8 h-8 text-primary-600" data-id="element-1069" />} maxWidth="xl" data-id="element-1068">
      <SEO title={t('seo.template.title')} description={t('seo.template.desc')} data-id="element-1070" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-1071">
        {/* Input Section */}
        <div className="space-y-6" data-id="element-1072">
          <Card title={t('generator.form.title')} className="h-full" data-id="element-1073">
            <div className="space-y-5" data-id="element-1074">
              <div data-id="element-1075">
                <label className={labelClasses} data-id="element-1076">
                  {t('generator.form.scamType')}
                </label>
                <select className={inputClasses} value={formData.scamType} onChange={e => setFormData({
                ...formData,
                scamType: e.target.value
              })} data-id="element-1077">
                  <option value="" data-id="element-1078">{t('generator.selectType')}</option>
                  <option value="Social Engineering" data-id="element-1079">
                    {t('generator.scamType.socialEngineering')}
                  </option>
                  <option value="Investment Scam" data-id="element-1080">
                    {t('generator.scamType.investment')}
                  </option>
                  <option value="Job Scam" data-id="element-1081">
                    {t('generator.scamType.job')}
                  </option>
                  <option value="Phishing" data-id="element-1082">
                    {t('generator.scamType.phishing')}
                  </option>
                  <option value="E-commerce Scam" data-id="element-1083">
                    {t('generator.scamType.ecommerce')}
                  </option>
                  <option value="Identity Theft" data-id="element-1084">
                    {t('generator.scamType.identity')}
                  </option>
                </select>
              </div>

              <div data-id="element-1085">
                <label className={labelClasses} data-id="element-1086">
                  {t('generator.form.date')}
                </label>
                <input type="date" className={inputClasses} value={formData.date} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} data-id="element-1087" />
              </div>

              <div data-id="element-1088">
                <label className={labelClasses} data-id="element-1089">
                  {t('generator.form.platform')}
                </label>
                <input type="text" placeholder={t('generator.placeholder.platform')} className={inputClasses} value={formData.platform} onChange={e => setFormData({
                ...formData,
                platform: e.target.value
              })} data-id="element-1090" />
              </div>

              <div data-id="element-1091">
                <label className={labelClasses} data-id="element-1092">
                  {t('generator.form.amount')}
                </label>
                <input type="text" placeholder={t('generator.placeholder.amount')} className={inputClasses} value={formData.amount} onChange={e => setFormData({
                ...formData,
                amount: e.target.value
              })} data-id="element-1093" />
              </div>

              <div data-id="element-1094">
                <label className={labelClasses} data-id="element-1095">
                  {t('generator.form.description')}
                </label>
                <textarea rows={4} placeholder={t('generator.placeholder.description')} className={inputClasses} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} data-id="element-1096" />
              </div>

              <div className="pt-2" data-id="element-1097">
                <Button onClick={handleGenerate} fullWidth size="lg" rightIcon={<FileText className="w-5 h-5" data-id="element-1099" />} data-id="element-1098">
                  {t('generator.generateBtn')}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Output Section */}
        <div className="space-y-6 lg:sticky lg:top-24" data-id="element-1100">
          <WarningBanner message={t('disclaimer.data.title') + ': ' + t('generator.subtitle')} variant="info" dismissible={false} data-id="element-1101" />

          <Card className="bg-slate-50 border-slate-200 overflow-hidden flex flex-col h-full min-h-[500px]" data-id="element-1102">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200" data-id="element-1103">
              <h2 className="text-lg font-bold text-primary-900 flex items-center" data-id="element-1104">
                <FileText className="h-5 w-5 mr-2 text-primary-600" data-id="element-1105" />
                {t('generator.output.title')}
              </h2>
              {generatedTemplate && <Button variant="outline" size="sm" onClick={handleCopy} className="bg-white" data-id="element-1106">
                  {copied ? <>
                      <Check className="h-4 w-4 mr-2 text-green-600" data-id="element-1107" />
                      <span className="text-green-600" data-id="element-1108">
                        {t('generator.copiedBtn')}
                      </span>
                    </> : <>
                      <Copy className="h-4 w-4 mr-2" data-id="element-1109" />
                      {t('generator.copyBtn')}
                    </>}
                </Button>}
            </div>

            <div className="flex-grow bg-white border border-slate-200 rounded-lg p-6 shadow-inner font-mono text-sm whitespace-pre-wrap overflow-auto max-h-[600px]" data-id="element-1110">
              {generatedTemplate ? <div className="text-slate-800 leading-relaxed" data-id="element-1111">
                  {generatedTemplate}
                </div> : <div className="h-full flex flex-col items-center justify-center text-slate-400 py-12" data-id="element-1112">
                  <FileText className="w-12 h-12 mb-4 opacity-20" data-id="element-1113" />
                  <p className="text-center italic max-w-xs" data-id="element-1114">
                    {t('generator.emptyState')}
                  </p>
                </div>}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 flex items-start" data-id="element-1115">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-slate-400" data-id="element-1116" />
              <p data-id="element-1117">{t('generator.templateNote')}</p>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>;
}